/**
 * LineageScrubber v4 — Compatibility Shim
 * ─────────────────────────────────────────
 * The scrubber UI now lives inside SelectionTray's Journey tab.
 * This shim exists so Dashboard.jsx doesn't need to change its
 * <LineageScrubber> render call — it simply translates those props
 * into the tray's journey props via the onOpenJourney callback.
 *
 * If you are wiring Dashboard v6+, prefer passing journeyLineage /
 * journeyProject / onJourneySelect directly to <SelectionTray> and
 * removing this component entirely.
 *
 * Place at: src/components/journey/LineageScrubber.jsx
 */

import { useEffect } from 'react';

/**
 * Props mirror the old LineageScrubber signature exactly so Dashboard
 * needs zero changes. The new prop `onOpenJourney` bridges to the tray.
 *
 * @param {object[]}  lineage          - Ordered project chain
 * @param {object}    currentProject   - Currently active project
 * @param {function}  onSelectProject  - User navigated in scrubber
 * @param {function}  onClose          - User dismissed scrubber
 * @param {function}  onViewProject    - User double-clicked card
 * @param {function}  onOpenJourney    - NEW: (lineage, currentProject) → opens tray Journey tab
 */
const LineageScrubber = ({
  lineage,
  projects: projectsProp,
  currentProject,
  currentProjectId,
  onSelectProject,
  onClose,
  onViewProject,
  onOpenJourney,   // injected by Dashboard when wiring to SelectionTray
  isVisible = true,
}) => {
  const chain = projectsProp || lineage || [];

  // When this component mounts or data changes, tell the tray to open its Journey tab.
  useEffect(() => {
    if (!isVisible || chain.length < 2) return;
    const active = currentProject || chain.find(p => p.id === currentProjectId) || chain[0];
    onOpenJourney?.(chain, active);
  }, [isVisible, chain, currentProject, currentProjectId]);

  // This component renders nothing — the UI is in SelectionTray > Journey tab.
  return null;
};

export default LineageScrubber;